import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import {
  cropSquareToWebp,
  readFileAsDataURL,
  type CropAreaPixels,
} from '../../utils/imageCompress';
import { readClipboardImageBlob } from '../../utils/slideImageCompress';

/** react-easy-crop CJS default export 호환 */
const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

export type MemberProfileImageUploadModalProps = {
  open: boolean;
  memberName: string;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
};

const MemberProfileImageUploadModal: React.FC<MemberProfileImageUploadModalProps> = ({
  open,
  memberName,
  onClose,
  onSave,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setRawImageSrc('');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsSaving(false);
      setError('');
      cropPixelsRef.current = null;
    }
  }, [open]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    const dataUrl = await readFileAsDataURL(file);
    setRawImageSrc(dataUrl);
  }, []);

  useEffect(() => {
    if (!open || rawImageSrc) return;
    const onPaste = (e: ClipboardEvent) => {
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'clipboard-image.png', {
              type: blob.type || 'image/png',
            });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, rawImageSrc, handleFile]);

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  const handleSaveClick = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const blob = await cropSquareToWebp(rawImageSrc, cropPixelsRef.current, 192, 0.72);
      await onSave(blob);
      onClose();
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg === 'QUOTA_EXCEEDED'
          ? '이미지 저장 용량이 부족합니다. 사용량을 확인한 뒤 다시 시도해 주세요.'
          : '이미지 저장에 실패했습니다. 다시 시도해 주세요.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-member-photo-upload-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      role="presentation"
    >
      <div
        className="cp-member-photo-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${memberName} 프로필 사진 설정`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-member-photo-upload-header">
          <h3>{memberName} 프로필 사진 설정</h3>
          <button
            type="button"
            className="cp-member-photo-upload-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {!rawImageSrc ? (
          <div
            className={`cp-member-photo-upload-dropzone${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            이미지를 드래그·클릭하거나 Ctrl+V로 붙여넣기
          </div>
        ) : (
          <>
            <div className="cp-member-photo-upload-crop">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_croppedArea, croppedAreaPixels) => {
                  cropPixelsRef.current = croppedAreaPixels;
                }}
              />
            </div>
            <div className="cp-member-photo-upload-zoom">
              <span>확대</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>
          </>
        )}

        {error ? <p className="cp-member-photo-upload-error">{error}</p> : null}

        <div className="cp-member-photo-upload-footer">
          <button
            type="button"
            className="cp-member-photo-upload-btn is-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            취소
          </button>
          {rawImageSrc ? (
            <button
              type="button"
              className="cp-member-photo-upload-btn is-save"
              onClick={() => void handleSaveClick()}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          ) : null}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            input.value = '';
            if (file) void handleFile(file);
          }}
        />
      </div>
    </div>,
    document.body
  );
};

export default MemberProfileImageUploadModal;
